import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import ProcedureQueueSection from '../components/procedures/ProcedureQueueSection';
import { useTheme } from '../theme/ThemeContext';
import { usePermissions, usePermission } from '../context/PermissionContext';
import useSittingStore from '../stores/sittingStore';
import usePatientPlanStore from '../stores/patientPlanStore';
import useSittingActions from '../hooks/useSittingActions';
import { fetchSuggestedSlots } from '../services/api/appointmentApi';

/**
 * ProcedureQueuePage — my Procedure Queue (assignee view).
 *
 * Sections:
 *   1. Action pending (collect here)    — status = awaiting_payment
 *   2. Ready to execute                 — payment_cleared | scheduled (today)
 *   3. In progress                      — status = in_progress
 *   4. Awaiting my approval             — pending_approval, approver = me
 *   5. Upcoming scheduled               — scheduled, future-dated
 *   6. Recently done                    — completed | paid | cancelled
 *
 * Action orchestration lives in useSittingActions so ActiveProceduresBoard
 * (the dashboard team board) shares identical behavior.
 */
export default function ProcedureQueuePage() {
  const { isDark } = useTheme();
  const { hasPermission, role: sessionRole, memberId: sessionMemberId } = usePermissions();
  const navigate = useNavigate();

  const {
    myQueue,
    fetchMyQueue,
    setMyMemberId,
    isLoading,
    error,
    scheduledByDate,
    fetchScheduledForDate,
  } = useSittingStore();

  const todayStr = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const canManageAppt = usePermission('MANAGE_APPOINTMENT');
  const scheduleAppointmentForItem = usePatientPlanStore(
    (s) => s.scheduleAppointmentForItem,
  );

  const viewer = useMemo(
    () => ({ memberId: sessionMemberId || '', role: sessionRole || '' }),
    [sessionMemberId, sessionRole],
  );

  useEffect(() => {
    if (viewer.memberId) setMyMemberId(viewer.memberId);
  }, [viewer.memberId, setMyMemberId]);

  const reload = useCallback(() => {
    fetchMyQueue();
  }, [fetchMyQueue]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Phase 2.5 — clinic-wide scheduled-for-date fetch. Re-runs when the
  // user picks a different date.
  useEffect(() => {
    if (selectedDate) fetchScheduledForDate(selectedDate);
  }, [selectedDate, fetchScheduledForDate]);

  const { handleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
    navigate,
  });

  const sections = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    const actionPending = [];
    const ready = [];
    const inProgress = [];
    const awaitingApproval = [];
    const scheduled = [];
    const recentlyDone = [];

    for (const s of myQueue || []) {
      if (s.status === 'awaiting_payment') {
        actionPending.push(s);
      } else if (
        s.status === 'payment_cleared'
        || (s.status === 'scheduled' && (s.scheduled_for || '').slice(0, 10) <= today)
      ) {
        ready.push(s);
      } else if (s.status === 'in_progress') {
        inProgress.push(s);
      } else if (s.status === 'pending_approval') {
        awaitingApproval.push(s);
      } else if (s.status === 'scheduled') {
        scheduled.push(s);
      } else if (
        s.status === 'completed'
        || s.status === 'paid'
        || s.status === 'cancelled'
      ) {
        recentlyDone.push(s);
      }
    }
    return { actionPending, ready, inProgress, awaitingApproval, scheduled, recentlyDone };
  }, [myQueue]);

  if (!hasPermission('EXECUTE_SITTING') && !hasPermission('COLLECT_PAYMENT')) {
    return <Navigate to="/access-denied" replace />;
  }

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const { actionPending, ready, inProgress, awaitingApproval, scheduled, recentlyDone } = sections;

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>
      <AdminPageHeader />

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Procedure Queue</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your sittings, in order of what needs doing.
              {' · '}
              <span className="text-slate-400 dark:text-slate-500">
                {isLoading
                  ? 'Loading…'
                  : `${(myQueue || []).length} sitting${(myQueue || []).length === 1 ? '' : 's'} assigned to you`}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium disabled:opacity-60"
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        <ProcedureQueueSection
          title="Action pending"
          subtitle="Collect before execute. Either you or the receptionist can mark paid."
          sittings={actionPending}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="No pending payments."
        />

        <ProcedureQueueSection
          title="Ready to execute"
          sittings={ready}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="Nothing ready right now."
        />

        <ProcedureQueueSection
          title="In progress"
          subtitle="Started — mark complete when done."
          sittings={inProgress}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="Nothing in progress."
        />

        <ProcedureQueueSection
          title="Awaiting my approval"
          subtitle="Assignee finished — your sign-off closes the sitting."
          sittings={awaitingApproval}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="Nothing awaiting approval."
        />

        <ProcedureQueueSection
          title="Upcoming scheduled"
          sittings={scheduled}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="Nothing on the calendar yet."
        />

        <ProcedureQueueSection
          title="Recently done"
          subtitle="Completed, paid, or cancelled — read-only."
          sittings={recentlyDone}
          viewer={viewer}
          onAction={handleAction}
          emptyLabel="Nothing finished recently."
        />

        {/* Phase 2.5 — clinic-wide scheduled-for-date slice with slot-book CTA */}
        <ScheduledForDateSection
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          sittings={scheduledByDate?.[selectedDate] || []}
          canManageAppt={canManageAppt}
          scheduleAppointmentForItem={scheduleAppointmentForItem}
          onAfterBook={() => fetchScheduledForDate(selectedDate)}
        />

        {dialogs}
      </main>
    </div>
  );
}


function ScheduledForDateSection({
  selectedDate,
  onDateChange,
  sittings,
  canManageAppt,
  scheduleAppointmentForItem,
  onAfterBook,
}) {
  // Split into with/without appointment. "Without" = needs booking.
  const withApp = [];
  const noApp = [];
  for (const s of sittings || []) {
    if (s.linked_appointment_id) withApp.push(s);
    else noApp.push(s);
  }

  return (
    <div className="mt-6 border border-indigo-300 dark:border-indigo-700 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-slate-900 dark:text-slate-100">
      <div className="px-3 py-2 border-b border-indigo-200 dark:border-indigo-700 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          Scheduled for
        </span>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => onDateChange(e.target.value)}
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-slate-800 dark:border-slate-600 text-slate-800 dark:text-slate-100"
        />
        <button
          type="button"
          onClick={() => onDateChange(new Date().toISOString().slice(0, 10))}
          className="text-xs font-medium px-2 py-1 rounded border border-indigo-400 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
        >
          Today
        </button>
        <span className="flex-1" />
        <span className="text-xs text-slate-500">
          {sittings.length} sitting{sittings.length === 1 ? '' : 's'} · {noApp.length} needs appointment
        </span>
      </div>

      {noApp.length > 0 && (
        <div className="px-3 py-2 border-b border-indigo-200 dark:border-indigo-700">
          <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
            Needs appointment
          </div>
          <div className="flex flex-col gap-1">
            {noApp.map((s) => (
              <NeedsAppointmentRow
                key={s.sitting_id}
                sitting={s}
                canManageAppt={canManageAppt}
                scheduleAppointmentForItem={scheduleAppointmentForItem}
                onAfterBook={onAfterBook}
              />
            ))}
          </div>
        </div>
      )}

      {withApp.length > 0 && (
        <div className="px-3 py-2">
          <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
            With appointment
          </div>
          <div className="flex flex-col gap-1">
            {withApp.map((s) => (
              <div
                key={s.sitting_id}
                className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 px-2 py-1"
              >
                <span className="flex-1 truncate">
                  {s.patient_name || s.patient_id} · {s.procedure_name}
                </span>
                <span className="text-xs text-slate-500">
                  {s.scheduled_for?.slice(11, 16) || ''}
                </span>
                <span className="text-xs text-emerald-700 dark:text-emerald-300">
                  Booked
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sittings.length === 0 && (
        <div className="px-3 py-6 text-center text-sm text-slate-500 italic">
          Nothing scheduled for {selectedDate}.
        </div>
      )}
    </div>
  );
}


function NeedsAppointmentRow({
  sitting,
  canManageAppt,
  scheduleAppointmentForItem,
  onAfterBook,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const doctorId = sitting.assigned_to_member_id || sitting.visit_doctor_member_id;
  const slotDate = (sitting.scheduled_for || '').slice(0, 10);

  const openPicker = async () => {
    setErr('');
    setPickerOpen(true);
    if (!slots.length && doctorId && slotDate) {
      try {
        const data = await fetchSuggestedSlots(doctorId, slotDate);
        const arr = Array.isArray(data?.slots) ? data.slots : (Array.isArray(data) ? data : []);
        setSlots(arr);
      } catch (_) {
        setSlots([]);
      }
    }
  };

  const pickSlot = async (slot) => {
    // Backend contract: SuggestedSlot = {time: "HH:MM", existing_count}.
    // Legacy shape {hour, label} supported as fallback.
    const slotTime = slot.time
      || (slot.hour !== undefined
        ? (String(slot.hour).includes(':')
            ? String(slot.hour)
            : `${String(slot.hour).padStart(2, '0')}:00`)
        : (slot.label || ''));
    if (!slotTime) {
      setErr('Slot has no time field.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const r = await scheduleAppointmentForItem(
        sitting.plan_id,
        sitting.item_id,
        { slot_date: slotDate, slot_time: slotTime },
      );
      if (!r.success) {
        setErr(r.error || 'Failed to book slot.');
      } else {
        setPickerOpen(false);
        if (onAfterBook) onAfterBook();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-sm text-slate-700 dark:text-slate-200 px-2 py-1">
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate">
          {sitting.patient_name || sitting.patient_id} · {sitting.procedure_name}
        </span>
        {canManageAppt && (
          <button
            type="button"
            onClick={openPicker}
            className="text-xs font-medium px-2.5 py-1 rounded border border-indigo-500 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
          >
            Book slot
          </button>
        )}
      </div>
      {pickerOpen && (
        <div className="mt-1 ml-2 pl-2 border-l-2 border-indigo-300">
          {!doctorId && (
            <div className="text-xs text-amber-700">
              No doctor assigned — can't fetch slots.
            </div>
          )}
          {doctorId && slots.length === 0 && (
            <div className="text-xs text-slate-500">Loading slots…</div>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {slots.map((slot) => {
              const slotTime = slot.time
                || (slot.hour !== undefined
                  ? (String(slot.hour).includes(':')
                      ? String(slot.hour)
                      : `${String(slot.hour).padStart(2, '0')}:00`)
                  : (slot.label || ''));
              const existing = typeof slot.existing_count === 'number'
                ? slot.existing_count
                : slot.appointment_count;
              return (
                <button
                  key={slotTime}
                  type="button"
                  disabled={busy}
                  onClick={() => pickSlot(slot)}
                  className="text-xs px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-indigo-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  {slotTime}
                  {typeof existing === 'number' && (
                    <span className="ml-1 text-[10px] opacity-70">
                      ({existing} booked)
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {err && (
            <div className="mt-1 text-xs text-red-600">{err}</div>
          )}
        </div>
      )}
    </div>
  );
}
